"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import clsx from "clsx";
import IconVolumeFull from "@ui/central-icons/icons/IconVolumeFull";
import IconVolumeOff from "@ui/central-icons/icons/IconVolumeOff";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useVideoMuteCoordinator } from "./VideoMuteCoordinator";

export interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  hasAudio?: boolean;
  onEnded?: () => void;
  shouldPlay?: boolean;
  className?: string;
  rounded?: boolean;
}

export function VideoPlayer({
  src,
  autoPlay = false,
  loop = false,
  muted: initialMuted = true,
  hasAudio = false,
  onEnded,
  shouldPlay,
  className,
  rounded = true,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(initialMuted);
  const coordinator = useVideoMuteCoordinator();
  const coordinatorId = useId();

  useEffect(() => {
    if (!videoRef.current) return;
    if (shouldPlay) {
      videoRef.current.play().catch(() => {});
    } else if (shouldPlay === false) {
      videoRef.current.pause();
    }
  }, [shouldPlay]);

  const muteFromCoordinator = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
    }
    setIsMuted(true);
  }, []);

  useEffect(() => {
    if (!coordinator) return;
    return coordinator.register(coordinatorId, muteFromCoordinator);
  }, [coordinator, coordinatorId, muteFromCoordinator]);

  const handleMuteToggle = () => {
    if (videoRef.current) {
      const nextMuted = !isMuted;
      videoRef.current.muted = nextMuted;
      setIsMuted(nextMuted);
      if (!nextMuted) {
        coordinator?.notifyUnmuted(coordinatorId);
      }
    }
  };

  const handleVideoEnded = () => {
    if (onEnded) {
      onEnded();
    }
  };

  return (
    <div className={clsx("tw-relative tw-w-full tw-h-full", className)}>
      <video
        ref={videoRef}
        src={src}
        className={clsx("tw-w-full tw-h-full tw-object-cover", rounded && "tw-rounded-2xl")}
        autoPlay={autoPlay}
        loop={loop}
        muted={isMuted}
        playsInline
        onEnded={handleVideoEnded}
      />

      {hasAudio && (
        <button
          onClick={handleMuteToggle}
          className='tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-shadow-sm hover:tw-scale-105 tw-transition-transform tw-duration-300 tw-bg-paper tw-outline-none focus-visible:tw-outline-focus'
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          <span className='tw-relative tw-w-4 tw-h-4'>
            <IconVolumeFull
              width={16}
              height={16}
              className={clsx(
                iconStrokeFix,
                "tw-absolute tw-inset-0 tw-transition-opacity tw-duration-200 tw-text-black",
                isMuted ? "tw-opacity-0" : "tw-opacity-100",
              )}
            />
            <IconVolumeOff
              width={16}
              height={16}
              className={clsx(
                iconStrokeFix,
                "tw-absolute tw-inset-0 tw-transition-opacity tw-duration-200 tw-text-black",
                isMuted ? "tw-opacity-100" : "tw-opacity-0",
              )}
            />
          </span>
        </button>
      )}
    </div>
  );
}
